/**
 * 統一的 API 基礎配置
 * 提供統一的 baseQuery 配置，包含 Header 設置、錯誤處理等
 */

import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Toast } from 'antd-mobile';

import { ApiErrorCode, IRtkQueryError } from '@/types/errors';
import { ConstantUtil } from '@/utils/ConstantUtil';
import { createApiError } from '@/utils/apiResponse';
import { deviceService } from '@/utils/deviceService';
import { ErrorHandler } from '@/utils/errorHandler';
import { logService } from '@/utils/logService';

// 由 Vite define 注入：__DEV__ = import.meta.env.DEV
// Jest 環境不會有 import.meta，因此用此常數避免 SyntaxError
declare const __DEV__: boolean | undefined;

/**
 * 創建設備信息
 */
async function createDeviceData(): Promise<string> {
  const platform = deviceService.getPlatform();
  const uniqueId = await deviceService.getUniqueId();

  const deviceData = {
    uniqueId,
    deviceType: platform === 'web' ? 'web' : platform === 'ios' ? 'iOS' : 'Android',
    deviceName: platform === 'web' ? 'web_browser' : `${platform}_device`,
    osType: platform === 'web' ? 'web' : platform === 'ios' ? 'ios' : 'android',
    channel: ConstantUtil.CHANNELS.AILE_LITE,
    bundleId: 'com.uuspa.web',
    language: 'zh-tw',
    version: '1.0.0',
  };

  return encodeURIComponent(JSON.stringify(deviceData));
}

/**
 * 創建基礎查詢配置（使用 satoken 認證）
 */
const baseQuery = fetchBaseQuery({
  baseUrl: `${ConstantUtil.API_BASE_URL}`,
  prepareHeaders: async (headers, { extra }) => {
    // 從 localStorage 獲取 token
    const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN);
    if (token) {
      headers.set('satoken', token);
    }

    // 設置設備信息
    const deviceData = await createDeviceData();
    headers.set('deviceData', deviceData);

    // 如果是 FormData，不要設置 Content-Type，讓瀏覽器自動設置（包含 boundary）
    // RTK Query 會自動處理 FormData，不需要手動設置 Content-Type
    const args = extra as { body?: FormData | unknown } | undefined;
    if (args?.body instanceof FormData) {
      headers.delete('Content-Type');
    }

    return headers;
  },
  timeout: ConstantUtil.API_TIMEOUT,
});

/**
 * 創建品牌與分店 API 專用的基礎查詢配置（使用 Bearer token 認證）
 */
const brandBaseQuery = fetchBaseQuery({
  baseUrl: `${ConstantUtil.API_BASE_URL}`,
  prepareHeaders: async (headers, { extra }) => {
    // 從 localStorage 獲取 token，使用 Bearer token 格式
    const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // 設置設備信息
    const deviceData = await createDeviceData();
    headers.set('deviceData', deviceData);

    // 如果是 FormData，不要設置 Content-Type，讓瀏覽器自動設置（包含 boundary）
    const args = extra as { body?: FormData | unknown } | undefined;
    if (args?.body instanceof FormData) {
      headers.delete('Content-Type');
    }

    return headers;
  },
  timeout: ConstantUtil.API_TIMEOUT,
});

/**
 * 刷新 Token
 * 使用 refresh token 獲取新的 access token
 */
async function refreshAccessToken(): Promise<boolean> {
  try {
    const refreshToken = localStorage.getItem(ConstantUtil.STORAGE_REFRESH_TOKEN);
    if (!refreshToken) {
      logService.warn('No refresh token available');
      return false;
    }

    logService.info('Refreshing access token...');

    const response = await fetch(`${ConstantUtil.API_BASE_URL}${ConstantUtil.API_REFRESH_TOKEN}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        deviceData: await createDeviceData(),
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      logService.error('Token refresh failed', { status: response.status });
      return false;
    }

    const data = await response.json();

    if (data.success && data.data?.tokens?.accessToken) {
      // 保存新的 access token
      localStorage.setItem(ConstantUtil.STORAGE_USER_TOKEN, data.data.tokens.accessToken);

      // 保存新的 refresh token（如果後端返回）
      if (data.data.tokens.refreshToken) {
        localStorage.setItem(ConstantUtil.STORAGE_REFRESH_TOKEN, data.data.tokens.refreshToken);
      }

      // 更新 token 過期時間（15 分鐘後）
      const expiresAt = Date.now() + ConstantUtil.TOKEN_EXPIRY_TIME;
      localStorage.setItem(ConstantUtil.STORAGE_TOKEN_EXPIRES_AT, expiresAt.toString());

      logService.info('Token refreshed successfully');
      return true;
    }

    return false;
  } catch (error) {
    logService.error('Token refresh error', error);
    return false;
  }
}

/**
 * 檢查 token 是否需要刷新
 * 如果 token 即將在 2 分鐘內過期，則返回 true
 */
function shouldRefreshToken(): boolean {
  const expiresAt = localStorage.getItem(ConstantUtil.STORAGE_TOKEN_EXPIRES_AT);
  if (!expiresAt) {
    return false;
  }

  const expiryTime = parseInt(expiresAt, 10);
  const now = Date.now();
  const timeUntilExpiry = expiryTime - now;

  // 如果 token 已過期或將在 2 分鐘內過期，則需要刷新
  return timeUntilExpiry <= ConstantUtil.TOKEN_REFRESH_THRESHOLD;
}

/**
 * 統一的 API 響應攔截器
 * 處理錯誤響應、Token 過期等情況
 */
export const baseQueryWithErrorHandling: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // 在發送請求前，檢查 token 是否需要刷新
  if (shouldRefreshToken()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      // 刷新失敗，清除本地存儲並重定向到登入頁
      localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN);
      localStorage.removeItem(ConstantUtil.STORAGE_REFRESH_TOKEN);
      localStorage.removeItem(ConstantUtil.STORAGE_TOKEN_EXPIRES_AT);
      localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO);

      Toast.show({
        content: '登入已過期，請重新登入',
        position: 'center',
        duration: 2000,
      });

      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);

      return {
        error: {
          status: 'CUSTOM_ERROR' as const,
          error: 'Token refresh failed',
        } as FetchBaseQueryError,
      };
    }
  }

  let result;

  // 調試：記錄請求詳情
  const endpoint = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : args.method || 'GET';
  const body = typeof args === 'string' ? undefined : args.body;

  if (body instanceof FormData) {
    logService.info('baseQueryWithErrorHandling: FormData request detected', {
      endpoint,
      method,
      formDataKeys: Array.from(body.keys()),
      hasLogo: body.has('logo'),
    });
  }

  try {
    result = await baseQuery(args, api, extraOptions);
  } catch (error) {
    // 捕獲網絡連接錯誤
    const endpoint = typeof args === 'string' ? args : args.url;
    logService.error('API connection error', error, { endpoint });

    // 在開發環境中提供更友好的錯誤提示
    if (__DEV__) {
      console.warn(
        `[開發模式] API 連接失敗: ${endpoint}\n` +
          '提示：如果後端服務器未啟動，請使用 mock 數據進行開發。\n' +
          '解決方案：將對應的 API 服務切換為 mockBaseQuery。'
      );
    }

    // 返回標準化的錯誤響應
    return {
      error: {
        status: 'FETCH_ERROR' as const,
        error: 'Connection failed. If the problem persists, please check your internet connection or VPN',
      } as FetchBaseQueryError,
    };
  }

  // 處理錯誤響應
  if (result.error) {
    const endpoint = typeof args === 'string' ? args : args.url;

    // 處理 FETCH_ERROR（網絡連接錯誤）
    if (result.error.status === 'FETCH_ERROR') {
      const errorMessage = __DEV__
        ? '開發模式：API 連接失敗，請檢查後端服務器是否啟動，或使用 mock 數據進行開發'
        : '網路連接失敗，請檢查網路設定或 VPN 連線';

      // 在開發環境中，不顯示 Toast，只記錄日誌
      if (__DEV__) {
        console.warn(
          `[開發模式] API 連接失敗: ${endpoint}\n` +
            '提示：如果後端服務器未啟動，請使用 mock 數據進行開發。\n' +
            '解決方案：將對應的 API 服務切換為 mockBaseQuery。'
        );
      } else {
        // 生產環境顯示用戶友好的錯誤提示
        Toast.show({
          content: errorMessage,
          position: 'center',
          duration: 3000,
        });
      }

      // 記錄錯誤日誌
      logService.error('API connection failed', {
        endpoint,
        error: result.error.error,
        status: result.error.status,
      });

      return result;
    }

    // 創建 API 錯誤對象
    const apiError = createApiError(result.error as IRtkQueryError, endpoint);

    // 檢查是否需要登出（401 相關錯誤）
    const shouldLogout = [ApiErrorCode.UNAUTHORIZED, ApiErrorCode.TOKEN_EXPIRED, ApiErrorCode.TOKEN_INVALID].includes(
      apiError.code as ApiErrorCode
    );

    if (shouldLogout) {
      // 清除本地存儲
      localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN);
      localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO);

      // 顯示登出提示
      Toast.show({
        content: '登入已過期，請重新登入',
        position: 'center',
        duration: 2000,
      });

      // 延遲後重定向到登入頁面
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    } else {
      // 非登出錯誤，顯示錯誤提示
      ErrorHandler.handleApiError(result.error, '操作失敗，請稍後再試', true);
    }

    // 記錄錯誤日誌
    logService.error('API request failed', apiError, { endpoint });
  }

  // 處理成功響應
  if (result.data) {
    const endpoint = typeof args === 'string' ? args : args.url;
    logService.logApiResponse(endpoint, 200, result.data);
  }

  return result;
};

/**
 * 品牌與分店 API 專用的錯誤處理 baseQuery（使用 Bearer token）
 */
export const brandBaseQueryWithErrorHandling: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // 在發送請求前，檢查 token 是否需要刷新
  if (shouldRefreshToken()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      // 刷新失敗，清除本地存儲並重定向到登入頁
      localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN);
      localStorage.removeItem(ConstantUtil.STORAGE_REFRESH_TOKEN);
      localStorage.removeItem(ConstantUtil.STORAGE_TOKEN_EXPIRES_AT);
      localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO);

      Toast.show({
        content: '登入已過期，請重新登入',
        position: 'center',
        duration: 2000,
      });

      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);

      return {
        error: {
          status: 'CUSTOM_ERROR' as const,
          error: 'Token refresh failed',
        } as FetchBaseQueryError,
      };
    }
  }

  let result;

  // 調試：記錄請求詳情
  const endpoint = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : args.method || 'GET';
  const body = typeof args === 'string' ? undefined : args.body;

  if (body instanceof FormData) {
    logService.info('brandBaseQueryWithErrorHandling: FormData request detected', {
      endpoint,
      method,
      formDataKeys: Array.from(body.keys()),
      hasLogo: body.has('logo'),
    });
  }

  try {
    result = await brandBaseQuery(args, api, extraOptions);
  } catch (error) {
    // 捕獲網絡連接錯誤
    const endpoint = typeof args === 'string' ? args : args.url;
    logService.error('Brand API connection error', error, { endpoint });

    // 在開發環境中提供更友好的錯誤提示
    if (__DEV__) {
      console.warn(
        `[開發模式] 品牌 API 連接失敗: ${endpoint}\n` +
          '提示：如果後端服務器未啟動，請使用 mock 數據進行開發。\n' +
          '解決方案：將對應的 API 服務切換為 mockBaseQuery。'
      );
    }

    // 返回標準化的錯誤響應
    return {
      error: {
        status: 'FETCH_ERROR' as const,
        error: 'Connection failed. If the problem persists, please check your internet connection or VPN',
      } as FetchBaseQueryError,
    };
  }

  // 處理錯誤響應
  if (result.error) {
    const endpoint = typeof args === 'string' ? args : args.url;

    // 處理 FETCH_ERROR（網絡連接錯誤）
    if (result.error.status === 'FETCH_ERROR') {
      const errorMessage = __DEV__
        ? '開發模式：品牌 API 連接失敗，請檢查後端服務器是否啟動，或使用 mock 數據進行開發'
        : '網路連接失敗，請檢查網路設定或 VPN 連線';

      // 在開發環境中，不顯示 Toast，只記錄日誌
      if (__DEV__) {
        console.warn(
          `[開發模式] 品牌 API 連接失敗: ${endpoint}\n` +
            '提示：如果後端服務器未啟動，請使用 mock 數據進行開發。\n' +
            '解決方案：將對應的 API 服務切換為 mockBaseQuery。'
        );
      } else {
        // 生產環境顯示用戶友好的錯誤提示
        Toast.show({
          content: errorMessage,
          position: 'center',
          duration: 3000,
        });
      }

      // 記錄錯誤日誌
      logService.error('Brand API connection failed', {
        endpoint,
        error: result.error.error,
        status: result.error.status,
      });

      return result;
    }

    // 創建 API 錯誤對象
    const apiError = createApiError(result.error as IRtkQueryError, endpoint);

    // 檢查是否需要登出（401 相關錯誤）
    const shouldLogout = [ApiErrorCode.UNAUTHORIZED, ApiErrorCode.TOKEN_EXPIRED, ApiErrorCode.TOKEN_INVALID].includes(
      apiError.code as ApiErrorCode
    );

    // 如果是 401 錯誤，嘗試刷新 token 並重試請求
    if (shouldLogout) {
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        // 刷新成功，重試原始請求
        logService.info('Token refreshed, retrying brand API request', { endpoint });
        try {
          result = await brandBaseQuery(args, api, extraOptions);

          // 如果重試成功，返回結果
          if (!result.error) {
            return result;
          }
        } catch (retryError) {
          logService.error('Retry brand API request failed after token refresh', retryError, { endpoint });
        }
      }

      // 刷新失敗或重試失敗，清除本地存儲並登出
      localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN);
      localStorage.removeItem(ConstantUtil.STORAGE_REFRESH_TOKEN);
      localStorage.removeItem(ConstantUtil.STORAGE_TOKEN_EXPIRES_AT);
      localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO);

      // 顯示登出提示
      Toast.show({
        content: '登入已過期，請重新登入',
        position: 'center',
        duration: 2000,
      });

      // 延遲後重定向到登入頁面
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    } else {
      // 非登出錯誤，顯示錯誤提示
      ErrorHandler.handleApiError(result.error, '操作失敗，請稍後再試', true);
    }

    // 記錄錯誤日誌
    logService.error('Brand API request failed', apiError, { endpoint });
  }

  // 處理成功響應
  if (result.data) {
    const endpoint = typeof args === 'string' ? args : args.url;
    logService.logApiResponse(endpoint, 200, result.data);
  }

  return result;
};

/**
 * 導出統一的 baseQuery（使用 satoken 認證）
 */
export { baseQueryWithErrorHandling as baseQuery };

/**
 * 導出品牌與分店 API 專用的 baseQuery（使用 Bearer token 認證）
 */
export { brandBaseQueryWithErrorHandling as brandBaseQuery };
